"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LoaderCircle, LogOut, Calendar } from "lucide-react";
import { useToast } from "@/components/ui/toast/use-toast";
import { signOut, useSession } from "next-auth/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { completeProfileSchema, type CompleteProfileInput } from "@/lib/validations/auth";
import { ErrorMessage } from "@/components/error-message";

type CompleteProfileFormData = CompleteProfileInput;

export function ProfileCompletionModal() {
  const { data: session, update } = useSession();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkProfileStatus = async () => {
      if (!session?.user?.id) {
        setNeedsProfileCompletion(false);
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch("/api/user/profile-status");
        if (res.ok) {
          const data = await res.json();
          setNeedsProfileCompletion(data.needsProfileCompletion === true);
        } else {
          setNeedsProfileCompletion(false);
        }
      } catch {
        setNeedsProfileCompletion(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkProfileStatus();
  }, [session?.user?.id]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CompleteProfileFormData>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      gender: undefined,
      birthDate: undefined,
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- watch() do React Hook Form não é memoizável por design
  const gender = watch("gender");

  const onSubmit = async (data: CompleteProfileFormData) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await fetch("/api/profile/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gender: data.gender,
          birthDate: data.birthDate.toISOString(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorMessage = result.errors?.formErrors?.[0] || result.message || "Erro ao completar perfil";
        setFormError(errorMessage);
        toast({
          title: "Erro",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Perfil completo!",
        description: "Suas informações foram salvas com sucesso.",
      });

      // Refetch profile status to close modal
      const res = await fetch("/api/user/profile-status");
      if (res.ok) {
        const data = await res.json();
        setNeedsProfileCompletion(data.needsProfileCompletion === true);
      }
      window.location.reload();
    } catch {
      const errorMessage = "Ocorreu um erro ao tentar salvar. Tente novamente.";
      setFormError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !needsProfileCompletion) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) { /* prevent close */ } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Completar perfil</DialogTitle>
          <DialogDescription className="text-center">
            Para continuar usando o Pediloo, precisamos de algumas informações adicionais.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 mt-4" noValidate>
          {formError && (
            <div className="mb-2">
              <ErrorMessage message={formError} />
            </div>
          )}
          <div className="grid gap-2">
            <Label className="flex items-center">
              Gênero
            </Label>
            <RadioGroup
              value={gender}
              onValueChange={(value) => setValue("gender", value as "MASCULINO" | "FEMININO")}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="MASCULINO" id="gender-male" />
                <Label htmlFor="gender-male" className="font-normal">Masculino</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="FEMININO" id="gender-female" />
                <Label htmlFor="gender-female" className="font-normal">Feminino</Label>
              </div>
            </RadioGroup>
            {errors.gender && (
              <p className="text-sm text-red-500">{errors.gender.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="birthDate" className="flex items-center">
              <Calendar className="mr-2 h-4 w-4" />
              Data de nascimento
            </Label>
            <Input
              id="birthDate"
              type="date"
              {...register("birthDate", { valueAsDate: true })}
            />
            {errors.birthDate && (
              <p className="text-sm text-red-500">{errors.birthDate.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              É necessário ter pelo menos 18 anos para usar o Pediloo.
            </p>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar e continuar"
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Button variant="ghost" size="sm" onClick={() => signOut({ callbackUrl: "/login" })}>
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}